"use client";

import { useState, useEffect, useMemo, ReactNode } from "react";
import { supabase } from "@/lib/supabaseClient";
import { SettingsContext } from "@/hooks/auth/useSettings";
import { Settings } from "@/types/auth";

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    const { data, error } = await supabase.from("settings").select("*").limit(1).single();
    if (error) {
      console.error("Error fetching settings:", error);
      setSettings(null);
    } else {
      setSettings(data);
    }
    setSettingsLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSettings = async (updates: Partial<Settings>) => {
    if (!settings?.id) return;

    const { data, error } = await supabase
      .from("settings")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", settings.id)
      .select()
      .single();

    if (error) {
      console.error("Error updating settings:", error);
      throw error;
    }

    // Update local state with the returned data
    setSettings(data);
  };

  const settingsContextValue = useMemo(
    () => ({
      settings,
      settingsLoading,
      updateSettings,
    }),
    [settings, settingsLoading]
  );

  return <SettingsContext.Provider value={settingsContextValue}>{children}</SettingsContext.Provider>;
};
